// frontend/src/pages/AdminPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    useTheme,
    Button,
    Tabs,
    Tab,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip as MuiTooltip,
    LinearProgress,
    TableSortLabel
} from '@mui/material';
import {
    TrendingUp,
    Email,
    Assignment,
    People,
    Business,
    Schedule,
    CheckCircle,
    Cancel,
    Refresh,
    ContactMail,
    Analytics,
    FilterList,
    Timer,
    BarChart as BarChartIcon,
    ShowChart,
    EmojiEvents
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    ReferenceLine
} from 'recharts';
import apiClient from '../services/apiClient';
import DetailsModal from '../components/modals/DetailsModal';
import SuperAdminUsersTable from '../components/admin/SuperAdminUsersTable';
import { format } from 'date-fns';
import {
    SEGMENT_OPTIONS,
    getSegmentLabel,
    getCorrectedFeedbackStats,
    getTimeToResponse,
    getPerCompanyStats,
    getBidsPerUserMonth,
    getBidSuccessAnalytics,
    DAYS_FILTER_OPTIONS
} from '../services/adminService';

// Local storage key for segment preference
const SEGMENT_STORAGE_KEY = 'admin_segment_preference';

const AdminPage = () => {
    const theme = useTheme();

    // Segment state (persisted to localStorage)
    const [segment, setSegment] = useState(() => {
        const saved = localStorage.getItem(SEGMENT_STORAGE_KEY);
        return saved && SEGMENT_OPTIONS.some(opt => opt.value === saved) ? saved : 'beta';
    });

    // State management
    const [dashboardData, setDashboardData] = useState(null);
    const [loginStats, setLoginStats] = useState(null);
    const [gcContactStats, setGcContactStats] = useState(null);
    const [dailyCharts, setDailyCharts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [lastRefresh, setLastRefresh] = useState(null);

    // New metrics state
    const [correctedFeedbackStats, setCorrectedFeedbackStats] = useState(null);
    const [timeToResponseStats, setTimeToResponseStats] = useState(null);
    const [perCompanyStats, setPerCompanyStats] = useState(null);
    const [bidsPerUserMonth, setBidsPerUserMonth] = useState(null);

    // Success analytics state
    const [successAnalytics, setSuccessAnalytics] = useState(null);
    const [successAnalyticsDays, setSuccessAnalyticsDays] = useState(365);
    const [successAnalyticsLoading, setSuccessAnalyticsLoading] = useState(false);

    // Per-company table sorting
    const [companySortField, setCompanySortField] = useState('bidsThisMonth');
    const [companySortDirection, setCompanySortDirection] = useState('desc');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    // Handle segment change
    const handleSegmentChange = useCallback((event, newSegment) => {
        if (newSegment !== null) {
            setSegment(newSegment);
            localStorage.setItem(SEGMENT_STORAGE_KEY, newSegment);
        }
    }, []);

    // Fetch data on component mount and when segment changes
    useEffect(() => {
        fetchAdminData();
    }, [segment]);

    const fetchAdminData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all admin data in parallel (with segment filter)
            const [
                dashboardResponse,
                loginResponse,
                gcContactResponse,
                dailyChartsResponse,
                correctedFeedbackResponse,
                timeToResponseResponse,
                perCompanyResponse,
                bidsPerUserMonthResponse
            ] = await Promise.all([
                apiClient.get('/api/admin/dashboard', { params: { segment } }),
                apiClient.get('/api/admin/login-stats', { params: { segment } }),
                apiClient.get('/api/admin/gc-contact-stats', { params: { segment } }),
                apiClient.get('/api/admin/daily-charts', { params: { segment } }),
                getCorrectedFeedbackStats(segment),
                getTimeToResponse(segment),
                getPerCompanyStats(segment),
                getBidsPerUserMonth(segment)
            ]);

            setDashboardData(dashboardResponse.data);
            setLoginStats(loginResponse.data);
            setGcContactStats(gcContactResponse.data);
            setDailyCharts(dailyChartsResponse.data);
            setCorrectedFeedbackStats(correctedFeedbackResponse);
            setTimeToResponseStats(timeToResponseResponse);
            setPerCompanyStats(perCompanyResponse);
            setBidsPerUserMonth(bidsPerUserMonthResponse);
            setLastRefresh(new Date());

        } catch (err) {
            console.error('Error fetching admin data:', err);
            setError(err.response?.data?.msg || 'Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch success analytics (separate from main data due to days filter)
    const fetchSuccessAnalytics = useCallback(async () => {
        try {
            setSuccessAnalyticsLoading(true);
            const data = await getBidSuccessAnalytics(segment, successAnalyticsDays || undefined);
            setSuccessAnalytics(data);
        } catch (err) {
            console.error('Error fetching success analytics:', err);
        } finally {
            setSuccessAnalyticsLoading(false);
        }
    }, [segment, successAnalyticsDays]);

    // Fetch success analytics when segment or days filter changes
    useEffect(() => {
        fetchSuccessAnalytics();
    }, [fetchSuccessAnalytics]);

    // Modal handling functions
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalData(null);
        setModalError(null);
    };

    const handleChartClick = (e) => {
        if (e && e.activePayload && e.activePayload.length > 0) {
            const payload = e.activePayload[0].payload;
            const dataKey = e.activePayload[0].dataKey;

            setModalLoading(true);
            setIsModalOpen(true);
            setModalError(null);

            apiClient.get('/api/admin/chart-details', {
                params: {
                    date: payload.date,
                    dataType: dataKey
                }
            })
            .then(response => {
                setModalData(response.data);
            })
            .catch(err => {
                setModalError(err.response?.data?.msg || 'Failed to load details.');
            })
            .finally(() => {
                setModalLoading(false);
            });
        }
    };

    const handleFeedbackCardClick = () => {
        setModalLoading(true);
        setIsModalOpen(true);
        setModalError(null);

        apiClient.get('/api/admin/feedback-rate-details')
            .then(response => {
                setModalData(response.data);
            })
            .catch(err => {
                setModalError(err.response?.data?.msg || 'Failed to load details.');
            })
            .finally(() => {
                setModalLoading(false);
            });
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    // Show error message
    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    // Extract data from responses
    const { usage, email, bids, topGcContacts } = dashboardData || {};

    // Define metric cards data
    const metricCards = [
        {
            title: 'Total Users',
            value: usage?.totalUsers || 0,
            icon: <People />,
            color: theme.palette.primary.main,
            subtitle: `+${usage?.recentUsers || 0} this month`
        },
        {
            title: 'Total Companies',
            value: usage?.totalCompanies || 0,
            icon: <Business />,
            color: theme.palette.secondary.main
        },
        {
            title: 'Total Bids',
            value: usage?.totalBids || 0,
            icon: <Assignment />,
            color: theme.palette.success.main,
            subtitle: `+${bids?.recentBidsSubmitted || 0} this month`
        },
        {
            title: 'Daily Active Users',
            value: usage?.dailyActiveUsers || 0,
            icon: <TrendingUp />,
            color: theme.palette.info.main
        },
        {
            title: 'Feedback Requests Sent',
            value: email?.totalFeedbackRequestsSent || 0,
            icon: <Email />,
            color: theme.palette.warning.main
        },
        {
            title: 'Feedback Response Rate',
            value: `${email?.overallResponseRate || 0}%`,
            icon: email?.overallResponseRate >= 50 ? <CheckCircle /> : <Cancel />,
            color: email?.overallResponseRate >= 50 ? theme.palette.success.main : theme.palette.error.main,
            clickable: true,
            onClick: handleFeedbackCardClick
        },
        {
            title: 'Avg Decision Time',
            value: bids?.avgDecisionTimeDays ? `${bids.avgDecisionTimeDays} days` : 'N/A',
            icon: <Schedule />,
            color: theme.palette.info.main,
            subtitle: bids?.decisionsAnalyzed ? `${bids.decisionsAnalyzed} decisions analyzed` : ''
        }
    ];

    // Render overview tab content
    const renderOverviewTab = () => (
        <>
            {/* Metric Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {metricCards.map((metric, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box
                                        sx={{
                                            backgroundColor: metric.color,
                                            borderRadius: 1,
                                            p: 1,
                                            mr: 2,
                                            color: 'white'
                                        }}
                                    >
                                        {metric.icon}
                                    </Box>
                                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                        {metric.title}
                                    </Typography>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                                    {metric.value}
                                </Typography>
                                {metric.subtitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {metric.subtitle}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Charts Row - Full Width */}
            <Box sx={{ width: '100vw', ml: 'calc(-50vw + 50%)', mb: 4 }}>
                <Card sx={{ mx: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
                            Daily Activity Overview (Last 30 Days)
                        </Typography>
                        <Box sx={{ height: 500, width: '100%', px: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart 
                                    data={dailyCharts?.chartData || []}
                                    margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                                    onClick={handleChartClick}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="dateDisplay" 
                                        tick={{ fontSize: 12 }}
                                        interval={1}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="userLogins"
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={3}
                                        name="User Logins"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="bidsCreated"
                                        stroke={theme.palette.secondary.main}
                                        strokeWidth={3}
                                        name="Bids Created"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="bidsSubmitted"
                                        stroke={theme.palette.success.main}
                                        strokeWidth={3}
                                        name="Bids Submitted"
                                        dot={{ r: 4 }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="feedbackRequestsSent" 
                                        stroke={theme.palette.warning.main}
                                        strokeWidth={3}
                                        name="Feedback Requests Sent"
                                        dot={{ r: 4 }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="feedbackResponsesReceived" 
                                        stroke={theme.palette.info.main}
                                        strokeWidth={3}
                                        name="Feedback Responses Received"
                                        dot={{ r: 4 }}
                                    />
                                    <Legend />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* 30-Day Summary Stats */}
            <Grid container spacing={3} sx={{ mt: -2 }}>
                <Grid item xs={12}>
                    {dailyCharts?.summary && (
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        30-Day Activity Summary
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dailyCharts.summary.businessDays} business days in period
                                    </Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Typography variant="h4" color="primary">
                                                {Math.round(dailyCharts.summary.avgDailyLogins * 10) / 10}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Logins/Business Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {dailyCharts.summary.totalLogins}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Typography variant="h4" color="secondary.main">
                                                {Math.round(dailyCharts.summary.avgDailyBidsCreated * 10) / 10}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Bids Created/Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {dailyCharts.summary.totalBidsCreated}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Typography variant="h4" color="success.main">
                                                {Math.round(dailyCharts.summary.avgDailyBidsSubmitted * 10) / 10}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Bids Submitted/Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {dailyCharts.summary.totalBidsSubmitted}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Typography variant="h4" color="warning.main">
                                                {Math.round(dailyCharts.summary.avgDailyRequests * 10) / 10}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Requests/Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {dailyCharts.summary.totalRequests}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Typography variant="h4" color="info.main">
                                                {Math.round(dailyCharts.summary.avgDailyResponses * 10) / 10}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Responses/Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total: {dailyCharts.summary.totalResponses}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </>
    );

    // Render daily charts tab content
    const renderDailyChartsTab = () => (
        <>
            {/* User Logins Chart - Full Width */}
            <Box sx={{ width: '100vw', ml: 'calc(-50vw + 50%)', mb: 4 }}>
                <Card sx={{ mx: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
                            Daily User Logins (Last 30 Days)
                        </Typography>
                        <Box sx={{ height: 350, width: '100%', px: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart 
                                    data={dailyCharts?.chartData || []}
                                    margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="dateDisplay" 
                                        tick={{ fontSize: 12 }}
                                        interval={1}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="userLogins" 
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={3}
                                        name="Daily Logins"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Bids Submitted Chart - Full Width */}
            <Box sx={{ width: '100vw', ml: 'calc(-50vw + 50%)', mb: 4 }}>
                <Card sx={{ mx: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
                            Daily Bids Submitted (Last 30 Days)
                        </Typography>
                        <Box sx={{ height: 350, width: '100%', px: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart 
                                    data={dailyCharts?.chartData || []}
                                    margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="dateDisplay" 
                                        tick={{ fontSize: 12 }}
                                        interval={1}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="bidsSubmitted" 
                                        stroke={theme.palette.success.main}
                                        strokeWidth={3}
                                        name="Bids Submitted"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Feedback Requests Chart - Full Width */}
            <Box sx={{ width: '100vw', ml: 'calc(-50vw + 50%)', mb: 4 }}>
                <Card sx={{ mx: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
                            Daily Feedback Requests Sent (Last 30 Days)
                        </Typography>
                        <Box sx={{ height: 350, width: '100%', px: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart 
                                    data={dailyCharts?.chartData || []}
                                    margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="dateDisplay" 
                                        tick={{ fontSize: 12 }}
                                        interval={1}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="feedbackRequestsSent" 
                                        stroke={theme.palette.warning.main}
                                        strokeWidth={3}
                                        name="Requests Sent"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Feedback Responses Chart - Full Width */}
            <Box sx={{ width: '100vw', ml: 'calc(-50vw + 50%)', mb: 4 }}>
                <Card sx={{ mx: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
                            Daily Feedback Responses Received (Last 30 Days)
                        </Typography>
                        <Box sx={{ height: 350, width: '100%', px: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart 
                                    data={dailyCharts?.chartData || []}
                                    margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="dateDisplay" 
                                        tick={{ fontSize: 12 }}
                                        interval={1}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid #ccc',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="feedbackResponsesReceived" 
                                        stroke={theme.palette.info.main}
                                        strokeWidth={3}
                                        name="Responses Received"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </>
    );

    // Render GC contacts performance tab
    const renderGcContactsTab = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6">
                                Contact Performance Rankings
                            </Typography>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    {gcContactStats?.totalGcContactsAnalyzed || 0} contacts analyzed
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Summary Stats */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Overall Avg Response Rate
                                    </Typography>
                                    <Typography variant="h5" color="primary">
                                        {gcContactStats?.overallStats?.avgResponseRate || 0}%
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Overall Avg Response Time
                                    </Typography>
                                    <Typography variant="h5" color="primary">
                                        {gcContactStats?.overallStats?.avgResponseTime || 0} days
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Performance Table */}
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Rank</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Requests Sent</TableCell>
                                        <TableCell align="right">Responses Received</TableCell>
                                        <TableCell align="right">Response Rate</TableCell>
                                        <TableCell align="right">Avg Response Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {gcContactStats?.topPerformers && gcContactStats.topPerformers.length > 0 ? (
                                        gcContactStats.topPerformers.map((contact, index) => (
                                            <TableRow key={contact._id}>
                                                <TableCell>
                                                    <Chip 
                                                        label={index + 1} 
                                                        size="small" 
                                                        color={index < 3 ? 'primary' : 'default'}
                                                        sx={{ minWidth: 32 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {contact.name || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {contact.feedbackRequestsSent}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {contact.feedbackResponsesReceived}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip 
                                                        label={`${contact.responseRate}%`}
                                                        color={contact.responseRate >= 70 ? 'success' : 
                                                              contact.responseRate >= 40 ? 'warning' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2">
                                                        {contact.avgResponseTimeDays ? `${contact.avgResponseTimeDays}d` : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No contact data available
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    // =========================================================================
    // ENHANCED METRICS TAB
    // =========================================================================
    const renderEnhancedMetricsTab = () => {
        const COLORS = [theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main, theme.palette.error.main];

        // Prepare pie chart data for time-to-response distribution
        const distributionData = timeToResponseStats?.distribution ? [
            { name: '< 24 hours', value: timeToResponseStats.distribution.within24Hours },
            { name: '24-48 hours', value: timeToResponseStats.distribution.within48Hours },
            { name: '2-7 days', value: timeToResponseStats.distribution.within7Days },
            { name: '> 7 days', value: timeToResponseStats.distribution.over7Days }
        ].filter(d => d.value > 0) : [];

        // Prepare funnel data
        const funnelData = correctedFeedbackStats?.funnel ? [
            { name: 'Sent', value: correctedFeedbackStats.funnel.requestsSent },
            { name: 'Opened', value: correctedFeedbackStats.funnel.opened },
            { name: 'Partial', value: correctedFeedbackStats.funnel.partialResponse },
            { name: 'Complete', value: correctedFeedbackStats.funnel.completeResponse }
        ] : [];

        return (
            <Grid container spacing={3}>
                {/* Corrected Feedback Rate Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CheckCircle sx={{ mr: 1, color: theme.palette.success.main }} />
                                <Typography variant="h6">Bid-Based Feedback Rate</Typography>
                            </Box>
                            {correctedFeedbackStats?.bidBasedMetrics ? (
                                <>
                                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                                        <Typography variant="h2" color="primary">
                                            {correctedFeedbackStats.bidBasedMetrics.responseRate}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {correctedFeedbackStats.bidBasedMetrics.bidsWithAnyResponse} of {correctedFeedbackStats.bidBasedMetrics.bidsWithRequestsSent} bids received feedback
                                        </Typography>
                                    </Box>

                                    {/* Timeframe Comparison */}
                                    <Typography variant="subtitle2" gutterBottom>Response Rates by Timeframe:</Typography>
                                    <Grid container spacing={1}>
                                        {Object.entries(correctedFeedbackStats.byTimeframe || {}).map(([key, data]) => (
                                            <Grid item xs={6} sm={3} key={key}>
                                                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                                    <Typography variant="h6" color="primary">{data.rate}%</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {key === 'last7Days' ? '7 Days' :
                                                         key === 'last30Days' ? '30 Days' :
                                                         key === 'last90Days' ? '90 Days' : 'All Time'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            ) : (
                                <CircularProgress />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Feedback Funnel Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ShowChart sx={{ mr: 1, color: theme.palette.info.main }} />
                                <Typography variant="h6">Feedback Funnel (Last 30 Days)</Typography>
                            </Box>
                            {funnelData.length > 0 ? (
                                <Box sx={{ height: 250 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={funnelData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" />
                                            <YAxis type="category" dataKey="name" width={80} />
                                            <Tooltip />
                                            <Bar dataKey="value" fill={theme.palette.primary.main}>
                                                {funnelData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            ) : (
                                <Typography color="text.secondary">No funnel data available</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Time to Response Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Timer sx={{ mr: 1, color: theme.palette.warning.main }} />
                                <Typography variant="h6">Time to Response</Typography>
                            </Box>
                            {timeToResponseStats ? (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                                <Typography variant="h4" color="primary">
                                                    {timeToResponseStats.averageHours != null
                                                        ? `${Math.round(timeToResponseStats.averageHours)}h`
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">Average</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                                <Typography variant="h4" color="secondary">
                                                    {timeToResponseStats.medianHours != null
                                                        ? `${Math.round(timeToResponseStats.medianHours)}h`
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">Median</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Distribution Pie Chart */}
                                    {distributionData.length > 0 && (
                                        <Box sx={{ height: 200 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={distributionData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={40}
                                                        outerRadius={80}
                                                        paddingAngle={2}
                                                        dataKey="value"
                                                        label={({ name, value }) => `${name}: ${value}`}
                                                    >
                                                        {distributionData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <CircularProgress />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Bids Per User Per Month Chart */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <BarChartIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                <Typography variant="h6">Bids Per User/Month</Typography>
                            </Box>
                            {bidsPerUserMonth?.monthly?.length > 0 ? (
                                <>
                                    <Box sx={{ height: 250 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={bidsPerUserMonth.monthly}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <ReferenceLine
                                                    y={bidsPerUserMonth.overall.avgBidsPerUserPerMonth}
                                                    stroke={theme.palette.warning.main}
                                                    strokeDasharray="5 5"
                                                    label={{ value: `Avg: ${bidsPerUserMonth.overall.avgBidsPerUserPerMonth}`, position: 'right' }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="avgBidsPerUser"
                                                    name="Avg Bids/User"
                                                    stroke={theme.palette.primary.main}
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="activeUsers"
                                                    name="Active Users"
                                                    stroke={theme.palette.secondary.main}
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total: {bidsPerUserMonth.overall.totalBids} bids across {bidsPerUserMonth.overall.totalActiveUserMonths} user-months
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <Typography color="text.secondary">No monthly data available</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Time to Response Trend */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Response Time Trend (Weekly)</Typography>
                            {timeToResponseStats?.trend?.length > 0 ? (
                                <Box sx={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={timeToResponseStats.trend}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="week" />
                                            <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                                            <Tooltip formatter={(value) => [`${value} hours`, 'Avg Response Time']} />
                                            <Line
                                                type="monotone"
                                                dataKey="avgHours"
                                                name="Avg Response Time"
                                                stroke={theme.palette.primary.main}
                                                strokeWidth={2}
                                                dot={{ r: 4 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            ) : (
                                <Typography color="text.secondary">No trend data available</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    // =========================================================================
    // PER-COMPANY TAB
    // =========================================================================
    const renderPerCompanyTab = () => {
        const handleSortChange = (field) => {
            if (companySortField === field) {
                setCompanySortDirection(companySortDirection === 'asc' ? 'desc' : 'asc');
            } else {
                setCompanySortField(field);
                setCompanySortDirection('desc');
            }
        };

        // Sort companies
        const sortedCompanies = [...(perCompanyStats?.companies || [])].sort((a, b) => {
            let aVal, bVal;
            if (companySortField === 'companyName') {
                aVal = a.companyName || '';
                bVal = b.companyName || '';
                return companySortDirection === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            } else {
                aVal = a.metrics?.[companySortField] ?? 0;
                bVal = b.metrics?.[companySortField] ?? 0;
                return companySortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
        });

        return (
            <Grid container spacing={3}>
                {/* Summary Cards */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">
                                {perCompanyStats?.summary?.totalCompanies || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Companies</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="success.main">
                                {perCompanyStats?.summary?.activeCompanies || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Active This Month</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="info.main">
                                {perCompanyStats?.summary?.avgBidsPerCompany || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Avg Bids/Company</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Company Table */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Company Breakdown</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={companySortField === 'companyName'}
                                                    direction={companySortField === 'companyName' ? companySortDirection : 'asc'}
                                                    onClick={() => handleSortChange('companyName')}
                                                >
                                                    Company
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center">Segment</TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={companySortField === 'activeUsers'}
                                                    direction={companySortField === 'activeUsers' ? companySortDirection : 'asc'}
                                                    onClick={() => handleSortChange('activeUsers')}
                                                >
                                                    Users
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={companySortField === 'totalBids'}
                                                    direction={companySortField === 'totalBids' ? companySortDirection : 'asc'}
                                                    onClick={() => handleSortChange('totalBids')}
                                                >
                                                    Total Bids
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={companySortField === 'bidsThisMonth'}
                                                    direction={companySortField === 'bidsThisMonth' ? companySortDirection : 'asc'}
                                                    onClick={() => handleSortChange('bidsThisMonth')}
                                                >
                                                    Bids (30d)
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={companySortField === 'feedbackResponseRate'}
                                                    direction={companySortField === 'feedbackResponseRate' ? companySortDirection : 'asc'}
                                                    onClick={() => handleSortChange('feedbackResponseRate')}
                                                >
                                                    Response Rate
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">Last Activity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedCompanies.length > 0 ? (
                                            sortedCompanies.map((company) => (
                                                <TableRow key={company.companyId}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {company.companyName || 'Unknown'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={company.segment}
                                                            size="small"
                                                            color={
                                                                company.segment === 'beta' ? 'primary' :
                                                                company.segment === 'internal' ? 'default' : 'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">{company.metrics?.activeUsers || 0}</TableCell>
                                                    <TableCell align="right">{company.metrics?.totalBids || 0}</TableCell>
                                                    <TableCell align="right">
                                                        <Chip
                                                            label={company.metrics?.bidsThisMonth || 0}
                                                            size="small"
                                                            color={company.metrics?.bidsThisMonth > 0 ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Chip
                                                            label={`${company.metrics?.feedbackResponseRate || 0}%`}
                                                            size="small"
                                                            color={
                                                                company.metrics?.feedbackResponseRate >= 70 ? 'success' :
                                                                company.metrics?.feedbackResponseRate >= 40 ? 'warning' : 'error'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">
                                                            {company.metrics?.lastActivity
                                                                ? format(new Date(company.metrics.lastActivity), 'MMM d, yyyy')
                                                                : 'Never'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No company data available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    // Render success analytics tab
    const renderSuccessAnalyticsTab = () => {
        // Helper to render confidence badge
        const renderConfidenceBadge = (confidence) => {
            const config = {
                high: { color: 'success', label: 'High confidence' },
                low: { color: 'warning', label: 'Low confidence' },
                insufficient: { color: 'error', label: 'Insufficient data' }
            };
            const { color, label } = config[confidence] || config.insufficient;
            return <Chip size="small" color={color} label={label} sx={{ ml: 1 }} />;
        };

        // Helper to render a metric card
        const renderMetricCard = (title, metricData, chartType = 'bar') => {
            if (!metricData) return null;

            const { data, sampleSize, confidence, insight } = metricData;

            return (
                <Card sx={{ height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{title}</Typography>
                            {renderConfidenceBadge(confidence)}
                        </Box>

                        {confidence === 'insufficient' ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">
                                    Not enough data to display this metric.
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Sample size: {sampleSize}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {Array.isArray(data) && data.length > 0 && (
                                    <Box sx={{ height: 200 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                                                <YAxis tick={{ fontSize: 11 }} />
                                                <Tooltip />
                                                <Bar dataKey="winRate" fill={theme.palette.primary.main} name="Win Rate %" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}

                                {/* For avgDaysToDecision which has different structure */}
                                {data?.distribution && (
                                    <Box sx={{ height: 200 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={data.distribution} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" />
                                                <YAxis tick={{ fontSize: 11 }} />
                                                <Tooltip />
                                                <Bar dataKey="count" fill={theme.palette.info.main} name="Count" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}

                                {/* For competitivenessGap */}
                                {data?.avgPercentageOff != null && (
                                    <Box sx={{ textAlign: 'center', py: 2 }}>
                                        <Typography variant="h3" color="warning.main">
                                            {data.avgPercentageOff.toFixed(1)}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Average gap from winning bid
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Median: {data.medianPercentageOff?.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                )}

                                {insight && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {insight}
                                    </Alert>
                                )}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Sample size: {sampleSize} bids
                                </Typography>
                            </>
                        )}
                    </CardContent>
                </Card>
            );
        };

        return (
            <>
                {/* Days Filter */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Bid Success Analytics</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Time Period:</Typography>
                        <ToggleButtonGroup
                            value={successAnalyticsDays}
                            exclusive
                            onChange={(e, value) => value !== null && setSuccessAnalyticsDays(value)}
                            size="small"
                        >
                            {DAYS_FILTER_OPTIONS.map((option) => (
                                <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none' }}>
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {successAnalyticsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : successAnalytics ? (
                    <>
                        {/* Overall Stats */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="primary">
                                                {successAnalytics.overallStats.totalDecidedBids}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Decided Bids
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="success.main">
                                                {successAnalytics.overallStats.overallWinRate}%
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Overall Win Rate
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="info.main">
                                                {successAnalytics.metrics.avgDaysToDecision?.data?.overall?.toFixed(0) || '—'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Days to Decision
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Metric Cards Grid */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('Win Rate by Revision Count', successAnalytics.metrics.winRateByRevision)}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('Win Rate by GC Coverage', successAnalytics.metrics.winRateByGcCoverage)}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('Days to Decision Distribution', successAnalytics.metrics.avgDaysToDecision)}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('Competitiveness Gap (Lost Bids)', successAnalytics.metrics.competitivenessGap)}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('Follow-up Impact on Win Rate', successAnalytics.metrics.followUpImpact)}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard('GC Responsiveness Correlation', successAnalytics.metrics.gcResponsivenessCorrelation)}
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Alert severity="info">No success analytics data available.</Alert>
                )}
            </>
        );
    };

    // Main render
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Modal for details */}
            <DetailsModal
                open={isModalOpen}
                onClose={handleCloseModal}
                data={modalData}
                loading={modalLoading}
                error={modalError}
            />

            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Platform Administration
                    </Typography>
                    {lastRefresh && (
                        <Typography variant="body2" color="text.secondary">
                            Last updated: {lastRefresh.toLocaleTimeString()}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Segment Toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterList fontSize="small" color="action" />
                        <ToggleButtonGroup
                            value={segment}
                            exclusive
                            onChange={handleSegmentChange}
                            size="small"
                            aria-label="data segment filter"
                        >
                            {SEGMENT_OPTIONS.map((option) => (
                                <ToggleButton
                                    key={option.value}
                                    value={option.value}
                                    sx={{ textTransform: 'none', px: 2 }}
                                >
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={fetchAdminData}
                        disabled={loading}
                    >
                        Refresh Data
                    </Button>
                </Box>
            </Box>

            {/* Segment Indicator */}
            {segment !== 'all' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Showing data for: <strong>{getSegmentLabel(segment)}</strong>
                </Alert>
            )}

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab icon={<Analytics />} label="Overview" />
                    <Tab icon={<TrendingUp />} label="Daily Charts" />
                    <Tab icon={<ContactMail />} label="Contact Performance" />
                    <Tab icon={<BarChartIcon />} label="Enhanced Metrics" />
                    <Tab icon={<Business />} label="Per-Company" />
                    <Tab icon={<EmojiEvents />} label="Success Analytics" />
                    <Tab icon={<People />} label="Users" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && renderOverviewTab()}
            {activeTab === 1 && renderDailyChartsTab()}
            {activeTab === 2 && renderGcContactsTab()}
            {activeTab === 3 && renderEnhancedMetricsTab()}
            {activeTab === 4 && renderPerCompanyTab()}
            {activeTab === 5 && renderSuccessAnalyticsTab()}
            {activeTab === 6 && <SuperAdminUsersTable />}
        </Container>
    );
};

export default AdminPage;